import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Building2, Pencil, Loader2, Hash, MapPin,
  Calendar, FolderOpen, ChevronRight, Folder, Plus,
  X as XIcon, Search,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import { toast } from 'sonner';
import { useCompany } from '../context/CompanyContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FIELDS = [
  { key: 'legal_name', label: 'Company Name', icon: Building2, placeholder: 'e.g. Acme Investments LLC' },
  { key: 'business_address', label: 'Company Address', icon: MapPin, placeholder: '123 Main St, City, ST 12345', textarea: true },
  { key: 'registered_agent', label: 'Registered Agent', icon: Building2, placeholder: 'Agent name' },
  { key: 'registered_agent_address', label: 'Registered Agent Address', icon: MapPin, placeholder: '123 Agent St, City, ST 12345', textarea: true },
  { key: 'formation_date', label: 'Date of Incorporation', icon: Calendar, type: 'date' },
  { key: 'formation_state', label: 'State of Incorporation', icon: MapPin, placeholder: 'e.g. Delaware' },
  { key: 'ein', label: 'Tax ID', icon: Hash, placeholder: 'XX-XXXXXXX' },
];

function emptyDetails() {
  return FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: '' }), { pinned_folder_ids: [] });
}

function normalizeName(s) {
  return (s || '').trim().toLowerCase();
}

export default function CompanyDetailsCard({ company }) {
  const { refreshCompanies } = useCompany();
  const [details, setDetails] = useState(company?.details || {});
  const [editOpen, setEditOpen] = useState(false);
  const [draft, setDraft] = useState(emptyDetails());
  const [saving, setSaving] = useState(false);

  // Folder pinning
  const [allFolders, setAllFolders] = useState([]); // all folders for this company
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');

  const loadFolders = useCallback(async () => {
    if (!company?.id) return;
    try {
      // We currently only have a list-by-parent endpoint; fetch root, then any subfolders is not needed for naming.
      // The user's intent: company-name folder is at root. Pinning supports any folder via "in_folder=all" would be nice — but we already have a get-folders endpoint per parent. We'll fetch all by walking the tree.
      const res = await axios.get(`${API_URL}/api/folders`, {
        params: { company_id: company.id },
        withCredentials: true,
      });
      // For now show root folders; that's where the "company-name" folder would live.
      setAllFolders(res.data || []);
    } catch {
      // silent
    }
  }, [company?.id]);

  useEffect(() => {
    setDetails(company?.details || {});
  }, [company?.id, company?.details]);

  useEffect(() => {
    loadFolders();
  }, [loadFolders]);

  // Persist pinned folder IDs
  const persistPinned = async (newIds) => {
    try {
      const nextDetails = { ...(details || {}), pinned_folder_ids: newIds };
      const res = await axios.put(
        `${API_URL}/api/companies/${company.id}/details`,
        { details: nextDetails },
        { withCredentials: true }
      );
      setDetails(res.data.details || nextDetails);
      refreshCompanies();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update pinned folders');
    }
  };

  const pinFolder = async (folder) => {
    const current = details?.pinned_folder_ids || [];
    if (current.includes(folder.id)) {
      toast.info('Folder is already pinned');
      return;
    }
    await persistPinned([...current, folder.id]);
    toast.success(`Pinned "${folder.name}" to Company Details`);
  };

  const unpinFolder = async (folderId) => {
    const current = details?.pinned_folder_ids || [];
    await persistPinned(current.filter((id) => id !== folderId));
  };

  // Auto-detect a folder matching the company name
  const companyNameFolder = (() => {
    if (!company?.name) return null;
    const target = normalizeName(company.name);
    return allFolders.find((f) => normalizeName(f.name) === target) || null;
  })();

  // Build display list: company-name folder (if exists) + pinned folders, deduped
  const pinnedIds = details?.pinned_folder_ids || [];
  const pinnedFolders = pinnedIds
    .map((id) => allFolders.find((f) => f.id === id))
    .filter(Boolean);

  const displayFolders = [];
  const seenIds = new Set();
  if (companyNameFolder) {
    displayFolders.push({ ...companyNameFolder, _auto: true });
    seenIds.add(companyNameFolder.id);
  }
  for (const f of pinnedFolders) {
    if (!seenIds.has(f.id)) {
      displayFolders.push(f);
      seenIds.add(f.id);
    }
  }

  // Auto-create a folder matching the company name
  const createCompanyFolder = async () => {
    try {
      const res = await axios.post(
        `${API_URL}/api/folders`,
        { company_id: company.id, name: company.name },
        { withCredentials: true }
      );
      toast.success(`Folder "${company.name}" created in Files`);
      // Refresh folders so it appears
      await loadFolders();
      // Pin it automatically
      await persistPinned([...(pinnedIds || []), res.data.id]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create folder');
    }
  };

  const openEdit = () => {
    const filled = { ...emptyDetails(), ...(details || {}) };
    Object.keys(filled).forEach((k) => {
      if (k !== 'pinned_folder_ids' && filled[k] == null) filled[k] = '';
    });
    setDraft(filled);
    setEditOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API_URL}/api/companies/${company.id}/details`,
        { details: { ...draft, pinned_folder_ids: pinnedIds } },
        { withCredentials: true }
      );
      setDetails(res.data.details || {});
      refreshCompanies();
      toast.success('Company details saved');
      setEditOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const populated = FIELDS.filter((f) => details?.[f.key]);
  const hasAny = populated.length > 0;

  // Picker list (filter unpinned + matching search; exclude already-pinned + company-name folder)
  const pickerList = allFolders.filter((f) => {
    if (pinnedIds.includes(f.id)) return false;
    if (companyNameFolder && f.id === companyNameFolder.id) return false;
    if (pickerSearch && !f.name.toLowerCase().includes(pickerSearch.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm" data-testid="company-details-card">
      <div className="p-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-blue-600" />
          <h2 className="font-medium text-gray-900">Company Details</h2>
        </div>
        <Button variant="ghost" size="sm" onClick={openEdit} data-testid="edit-company-details-btn">
          <Pencil className="w-3.5 h-3.5 mr-1.5" />
          Edit
        </Button>
      </div>

      <div className="p-4">
        {hasAny ? (
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
            {populated.map(({ key, label, icon: Icon }) => (
              <div key={key} className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Icon className="w-4 h-4 text-gray-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <dt className="text-xs uppercase tracking-wide text-gray-400 font-medium">{label}</dt>
                  <dd className="text-sm text-gray-900 mt-0.5 break-words whitespace-pre-wrap">
                    {details[key]}
                  </dd>
                </div>
              </div>
            ))}
          </dl>
        ) : (
          <div className="text-center py-6">
            <Building2 className="w-10 h-10 text-gray-200 mx-auto mb-2" />
            <p className="text-sm text-gray-500 mb-3">
              Add company info like EIN, formation state, and registered agent for quick access.
            </p>
            <Button size="sm" variant="outline" onClick={openEdit}>
              <Pencil className="w-3.5 h-3.5 mr-1.5" />
              Add Company Details
            </Button>
          </div>
        )}
      </div>

      {/* Folders quick access */}
      <div className="border-t border-gray-100 px-4 py-3" data-testid="company-folders-section">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <FolderOpen className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-gray-900">Folders</span>
            {displayFolders.length > 0 && (
              <span className="text-xs text-gray-400">
                ({displayFolders.length})
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {!companyNameFolder && (
              <Button
                variant="ghost"
                size="sm"
                onClick={createCompanyFolder}
                className="text-xs text-gray-600 hover:text-gray-900"
                data-testid="create-company-folder-btn"
                title={`Create a folder named "${company?.name}"`}
              >
                <Plus className="w-3.5 h-3.5 mr-1" />
                Create "{company?.name}"
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setPickerSearch('');
                setPickerOpen(true);
              }}
              className="text-xs text-blue-600 hover:text-blue-700"
              data-testid="pin-folder-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-1" />
              Pin folder
            </Button>
          </div>
        </div>

        {displayFolders.length > 0 ? (
          <ul className="space-y-1 mt-2" data-testid="pinned-folders-list">
            {displayFolders.map((f) => (
              <li
                key={f.id}
                className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-gray-50 group"
              >
                <Link
                  to={`/files?folder=${f.id}`}
                  className="flex items-center gap-2 min-w-0 flex-1 text-gray-700 hover:text-blue-700"
                  data-testid={`pinned-folder-${f.id}`}
                >
                  <Folder className="w-4 h-4 text-blue-500 flex-shrink-0" />
                  <span className="text-sm truncate">{f.name}</span>
                  <span className="text-xs text-gray-400 ml-1">
                    {f.file_count} item{f.file_count === 1 ? '' : 's'}
                  </span>
                </Link>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Link
                    to={`/files?folder=${f.id}`}
                    className="text-xs text-blue-600 hover:text-blue-700 opacity-0 group-hover:opacity-100"
                  >
                    Open <ChevronRight className="inline w-3 h-3" />
                  </Link>
                  {!f._auto && (
                    <button
                      onClick={() => unpinFolder(f.id)}
                      className="text-gray-400 hover:text-red-600 p-0.5 rounded opacity-0 group-hover:opacity-100"
                      title="Unpin folder"
                      data-testid={`unpin-folder-${f.id}`}
                    >
                      <XIcon className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-gray-400 mt-1">
            Pin folders from Files for one-click access here. The folder named "{company?.name}" will appear automatically once it exists.
          </p>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Company Details — {company?.name}</DialogTitle>
            <DialogDescription>
              Store key business info for quick reference. Empty fields are hidden from the dashboard.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
            {FIELDS.map((f) => (
              <div
                key={f.key}
                className={`space-y-1.5 ${f.textarea ? 'sm:col-span-2' : ''}`}
              >
                <Label htmlFor={`field-${f.key}`}>{f.label}</Label>
                {f.textarea ? (
                  <Textarea
                    id={`field-${f.key}`}
                    rows={2}
                    value={draft[f.key] || ''}
                    placeholder={f.placeholder}
                    onChange={(e) => setDraft((p) => ({ ...p, [f.key]: e.target.value }))}
                    data-testid={`details-${f.key}-input`}
                  />
                ) : (
                  <Input
                    id={`field-${f.key}`}
                    type={f.type || 'text'}
                    value={draft[f.key] || ''}
                    placeholder={f.placeholder}
                    onChange={(e) => setDraft((p) => ({ ...p, [f.key]: e.target.value }))}
                    data-testid={`details-${f.key}-input`}
                  />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-company-details-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save details'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Folder Picker Dialog */}
      <Dialog
        open={pickerOpen}
        onOpenChange={(open) => {
          setPickerOpen(open);
          if (!open) setPickerSearch('');
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Pin a folder</DialogTitle>
            <DialogDescription>
              Choose a folder from {company?.name}'s Files tab to pin to Company Details.
            </DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              autoFocus
              placeholder="Search folders..."
              value={pickerSearch}
              onChange={(e) => setPickerSearch(e.target.value)}
              className="pl-10"
              data-testid="folder-picker-search"
            />
          </div>
          <div className="border border-gray-200 rounded-lg max-h-72 overflow-y-auto">
            {pickerList.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-6">
                {allFolders.length === 0
                  ? 'No folders yet. Create one from the Files tab first.'
                  : 'No more folders to pin.'}
              </p>
            ) : (
              pickerList.map((f) => (
                <button
                  key={f.id}
                  onClick={async () => {
                    await pinFolder(f);
                    setPickerOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 border-b border-gray-100 last:border-0 text-left"
                  data-testid={`pick-folder-${f.id}`}
                >
                  <Folder className="w-4 h-4 text-blue-500" />
                  <span className="text-sm font-medium text-gray-900 flex-1 truncate">{f.name}</span>
                  <span className="text-xs text-gray-400 flex-shrink-0">
                    {f.file_count} item{f.file_count === 1 ? '' : 's'}
                  </span>
                </button>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPickerOpen(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
